import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { CheckCircle2, AlertCircle, Loader2, Mail } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import AppShell from "@/components/AppShell";

export default function VerifyEmailPage() {
  const { token } = useParams();
  const nav = useNavigate();
  const [state, setState] = useState({ phase: "verifying", msg: "" });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.post("/auth/verify-email", { token });
        if (alive) setState({ phase: "ok", already: !!data.already_verified });
      } catch (e) {
        if (alive) setState({
          phase: "err",
          msg: formatApiErrorDetail(e.response?.data?.detail) || "Something went wrong.",
        });
      }
    })();
    return () => { alive = false; };
  }, [token]);

  return (
    <AppShell>
      <div className="mx-auto max-w-md px-6 py-14 text-center" data-testid="verify-email-page">
        {state.phase === "verifying" && (
          <>
            <Loader2 className="w-10 h-10 mx-auto animate-spin mb-4" style={{ color: "var(--accent-copper)" }} />
            <h1 className="font-display text-2xl font-extrabold mb-2">Confirming your email…</h1>
            <p className="text-sm" style={{ color: "var(--text-secondary)" }}>Just a moment.</p>
          </>
        )}
        {state.phase === "ok" && (
          <div data-testid="verify-ok-state">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full mb-4"
                 style={{ background: "#EFF9F1", color: "#0F5D2E" }}>
              <CheckCircle2 className="w-7 h-7" />
            </div>
            <h1 className="font-display text-2xl sm:text-3xl font-extrabold mb-2">
              {state.already ? "Already verified" : "Email verified"}
            </h1>
            <p className="text-sm mb-6" style={{ color: "var(--text-secondary)" }}>
              {state.already
                ? "Your email was already confirmed. You're all set."
                : "Thanks for confirming — your account is fully set up."}
            </p>
            <button
              type="button"
              onClick={() => nav("/dashboard")}
              className="btn-primary"
              data-testid="verify-continue-btn"
            >
              Continue to dashboard
            </button>
          </div>
        )}
        {state.phase === "err" && (
          <div data-testid="verify-err-state">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full mb-4"
                 style={{ background: "#FDECE7", color: "#B54234" }}>
              <AlertCircle className="w-7 h-7" />
            </div>
            <h1 className="font-display text-2xl font-extrabold mb-2">This link isn't valid</h1>
            <p className="text-sm mb-6" style={{ color: "var(--text-secondary)" }}>{state.msg}</p>
            <Link
              to="/login"
              className="btn-primary inline-flex items-center gap-1.5"
              data-testid="verify-back-to-login-btn"
            >
              <Mail className="w-4 h-4" /> Back to sign in
            </Link>
          </div>
        )}
      </div>
    </AppShell>
  );
}
